import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import API from '../api';
import { logDownload } from '../utils/eventLog';
import FileDropZone from '../components/FileDropZone';
import * as emailCache from '../utils/emailClientCache';
import * as mailSync from '../utils/mailSync';
import SysMail from './SysMail';

// ── 전자메일 메뉴 ────────────────────────────────────────────────────────
// 2026-08 신규 요청: "메뉴에 전자메일 메뉴를 추가해줘" — 실제 이메일 계정(IMAP/SMTP)에
// 연동해서 메일함 조회/작성/발송을 할 수 있는 화면. 메일 본문 목록은 서버 DB에 저장되지
// 않고 매번 IMAP 서버에서 실시간으로 가져온다(backend/routes/email.js 참고).
//
// 2026-08 추가 요청: "여러개의 전자메일을 가져오는 경우를 위해" — 한 사람이 여러 이메일
// 계정을 등록해서 계정 탭으로 전환하며 쓸 수 있게 개편했다.
//  1) 계정설정에서 계정을 여러 개 추가할 수 있다(AccountsModal/AccountForm).
//  2) 좌측에 있던 폴더 목록을 다른 메뉴(업체관리/계약관리/인증관리 등)와 동일한 상단
//     밑줄 탭 스타일로 옮겼다 — 받은편지함/보낸편지함/보낼편지함/휴지통 4종 구성이 기본.
//  3) 서버가 이 4종 구성을 제공하지 못하면(POP3처럼 폴더가 아예 없거나, IMAP 계정에
//     커스텀 폴더가 섞여 있으면) 백엔드가 실제 폴더 이름을 그대로 내려주므로, 폴더 탭도
//     자동으로 그 실제 이름으로 표시된다(별도 분기 없이 GET /email/folders 응답을 그대로
//     탭 라벨로 사용). 계정 탭 이름은 계정설정의 "탭에 표시할 이름"(기본값: 메일주소)을 쓴다.
//
// 2026-08 추가 수정 3건:
//  1) 메일 목록(제목)과 본문(내용) 영역 사이에 드래그로 크기를 조절할 수 있는 구분선을
//     추가했다 — 목록 폭은 localStorage에 기억해서 다음에 열어도 유지된다.
//  2) POP3 계정은 폴더가 받은편지함 하나뿐이라 폴더 탭 줄 자체가 불필요하므로, 폴더가
//     1개 이하일 때는 그 줄을 숨긴다.
//  3) IMAP 계정처럼 폴더가 여러 개(받은편지함/보낸편지함/휴지통 등)인 경우에는 지금처럼
//     그대로 탭 줄을 보여준다 — 2)와 반대되는 동작이 자동으로 되도록 folders.length 하나로
//     분기했다(계정 protocol을 별도로 참조할 필요가 없다).
//
// 2026-08 추가 수정 2건(메일쓰기):
//  1) 첨부파일을 다른 메뉴(업체관리/공지사항 등)와 동일한 드래그 앤 드롭 방식(FileDropZone,
//     여러 파일 가능)으로 바꿨다.
//  2) 계정설정에 "서명설정"을 추가했다 — 이미지도 넣을 수 있는 서명을 계정별로 등록해두면,
//     메일 작성 시 "서명 포함" 체크로 본문 아래에 자동으로 붙는다(공지사항 내용 편집기와
//     같은 방식으로 이미지를 붙여넣거나 드래그해서 넣을 수 있고, 발송 시점에 백엔드가 그
//     이미지를 실제 메일 첨부(cid)로 바꿔서 수신자 메일함에서도 항상 보이도록 처리한다).

const PAGE_SIZE = 30;
const LIST_WIDTH_KEY = 'ims_email_list_width';
const LIST_WIDTH_MIN = 260;
const LIST_WIDTH_MAX = 800;
const LIST_WIDTH_DEFAULT = 380;

function fmtAddr(list) {
  if (!list || !list.length) return '';
  return list.map(a => a.name ? `${a.name} <${a.address}>` : a.address).join(', ');
}
function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function ExternalEmail() {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  // 2026-09 요청: "계정설정 왼쪽에 주소록 버튼 추가" — 업체관리 담당자 자동가져오기/수기등록/
  // CSV·vCard 업로드/공용·개인 구분을 지원하는 주소록 모달 표시 여부.
  const [showAddressBook, setShowAddressBook] = useState(false);

  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [detail, setDetail] = useState(null); // { uid, subject, from, to, cc, date, html, text, attachments }
  const [detailLoading, setDetailLoading] = useState(false);

  const [compose, setCompose] = useState(null); // { to, cc, bcc, subject, body, files: File[] }
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState('');

  // 메일 목록(제목) / 본문(내용) 영역 폭 — 사용자가 가운데 구분선을 드래그해서 조절할 수
  // 있게 한다. 마지막으로 조절한 폭은 localStorage에 저장해서 다음에 메뉴를 열었을 때도
  // 그대로 유지된다.
  const [listWidth, setListWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem(LIST_WIDTH_KEY));
    return saved >= LIST_WIDTH_MIN && saved <= LIST_WIDTH_MAX ? saved : LIST_WIDTH_DEFAULT;
  });
  const splitContainerRef = useRef(null);
  const listWidthRef = useRef(listWidth);
  const draggingRef = useRef(false);
  useEffect(() => { listWidthRef.current = listWidth; }, [listWidth]);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const left = splitContainerRef.current.getBoundingClientRect().left;
      const next = Math.max(LIST_WIDTH_MIN, Math.min(LIST_WIDTH_MAX, e.clientX - left));
      setListWidth(next);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.localStorage.setItem(LIST_WIDTH_KEY, String(listWidthRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startListResize = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadAccounts = useCallback(() => {
    setAccountsLoading(true);
    API.get('/email/accounts')
      .then(res => {
        const list = res.data.accounts || [];
        setAccounts(list);
        setActiveAccountId(prev => (prev && list.some(a => a.id === prev)) ? prev : (list[0]?.id || null));
      })
      .catch(() => { setAccounts([]); setActiveAccountId(null); })
      .finally(() => setAccountsLoading(false));
  }, []);

  // 2026-09: "로그인하면 캐시로 내려받아두었다가 로그아웃되면 삭제" 요구사항의 캐시 위치를
  // 서버 메모리에서 이 브라우저 탭의 메모리(frontend/src/utils/emailClientCache.js)로 옮겼다
  // — 동시접속자가 많아져도 서버 자원은 전혀 늘지 않고, 캐시 유지 부담이 각자의 PC로 분산된다.
  // force=true(새로고침 버튼)면 캐시를 무시하고 항상 서버에서 새로 받아온다.
  const loadFolders = useCallback((accountId, force) => {
    if (!accountId) { setFolders([]); return Promise.resolve([]); }
    const cacheKey = emailCache.keyFolders(accountId);
    if (!force) {
      const cached = emailCache.get(cacheKey);
      if (cached) { setFolders(cached); return Promise.resolve(cached); }
    }
    // PC(디스크)에 미리 받아둔 목록이 있으면 먼저 즉시 보여주고 서버 응답으로 갱신(stale-while-revalidate)
    mailSync.persistGet(cacheKey).then(stale => { if (stale && !emailCache.get(cacheKey)) setFolders(stale); });
    return API.get('/email/folders', { params: { accountId } })
      .then(res => {
        const list = res.data.folders || [];
        setFolders(list);
        mailSync.saveFolders(accountId, list);
        return list;
      })
      .catch(() => { setFolders([]); return []; });
  }, []);

  const loadMessages = useCallback((accountId, f, p, force) => {
    if (!accountId) return;
    const cacheKey = emailCache.keyMessages(accountId, f, p, PAGE_SIZE);
    if (!force) {
      const cached = emailCache.get(cacheKey);
      if (cached) { setMessages(cached.messages); setTotal(cached.total); return; }
    }
    setListLoading(true); setListError('');
    // PC(디스크)에 미리 받아둔 목록이 있으면 서버 응답 전에 즉시 표시
    if (!force) mailSync.persistGet(cacheKey).then(stale => {
      if (stale && stale.messages && !emailCache.get(cacheKey)) { setMessages(stale.messages); setTotal(stale.total || 0); setListLoading(false); }
    });
    API.get('/email/messages', { params: { accountId, folder: f, page: p, pageSize: PAGE_SIZE } })
      .then(res => {
        const messages = res.data.messages || [];
        const total = res.data.total || 0;
        setMessages(messages);
        setTotal(total);
        mailSync.saveMessages(accountId, f, p, { messages, total });
      })
      .catch(err => setListError(err?.response?.data?.message || '메일 목록을 불러오지 못했습니다.'))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // 계정 탭을 바꾸면 그 계정의 폴더 목록을 새로 받아온 뒤, 이전에 보던 폴더가 그 계정에도
  // 있으면 유지하고 없으면 받은편지함(또는 첫 폴더)으로 되돌려서 메일 목록을 불러온다.
  useEffect(() => {
    if (!activeAccountId) { setFolders([]); setMessages([]); setTotal(0); setDetail(null); return; }
    let cancelled = false;
    setDetail(null);
    loadFolders(activeAccountId).then(list => {
      if (cancelled) return;
      const next = list.some(f => f.path === folder)
        ? folder
        : (list.find(f => f.path === 'INBOX')?.path || list[0]?.path || 'INBOX');
      setFolder(next);
      setPage(1);
      loadMessages(activeAccountId, next, 1);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  const changeFolder = (f) => { setFolder(f); setPage(1); setDetail(null); loadMessages(activeAccountId, f, 1); };
  const changePage = (p) => { setPage(p); setDetail(null); loadMessages(activeAccountId, folder, p); };

  const openMessage = (uid) => {
    setDetailLoading(true); setDetail(null);
    // PC에 미리 받아둔 본문이 있으면 즉시 표시(서버 응답은 읽음 처리·최신화용으로 계속 진행)
    const detailKey = mailSync.keyDetail(activeAccountId, folder, uid);
    mailSync.persistGet(detailKey).then(cached => { if (cached) { setDetail(cached); setDetailLoading(false); } });
    API.get(`/email/messages/${uid}`, { params: { accountId: activeAccountId, folder } })
      .then(res => {
        setDetail(res.data);
        mailSync.persistSet(detailKey, res.data);
        setMessages(ms => ms.map(m => m.uid === uid ? { ...m, seen: true } : m));
        // 서버에서 읽음(Seen) 플래그가 바뀌었으므로 이 계정의 캐시된 목록은 더 이상 최신이
        // 아니다 — 다음 조회부터는 새로 받아오게 무효화한다.
        mailSync.invalidateAccount(activeAccountId);
      })
      .catch(err => showToast(err?.response?.data?.message || '메일을 여는 중 오류가 발생했습니다.'))
      .finally(() => setDetailLoading(false));
  };

  const toggleFlag = (uid, flag, value) => {
    API.patch(`/email/messages/${uid}/flags`, { flag, value }, { params: { accountId: activeAccountId, folder } })
      .then(() => {
        setMessages(ms => ms.map(m => m.uid === uid ? { ...m, [flag]: value } : m));
        mailSync.invalidateAccount(activeAccountId);
      })
      .catch(err => showToast(err?.response?.data?.message || '반영 실패'));
  };

  const removeMessage = (uid) => {
    if (!window.confirm('이 메일을 삭제할까요?')) return;
    API.delete(`/email/messages/${uid}`, { params: { accountId: activeAccountId, folder } })
      .then(res => {
        showToast(res?.data?.message || '삭제되었습니다.');
        setDetail(d => (d && d.uid === uid) ? null : d);
        mailSync.invalidateAccount(activeAccountId);
        loadMessages(activeAccountId, folder, page, true);
      })
      .catch(err => showToast(err?.response?.data?.message || '삭제 실패'));
  };

  const downloadAttachment = (uid, idx, filename) => {
    API.get(`/email/messages/${uid}/attachments/${idx}`, {
      params: { accountId: activeAccountId, folder }, responseType: 'blob',
    })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = filename || 'attachment';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(blobUrl);
        logDownload('전자메일', filename);
      })
      .catch(() => showToast('첨부파일 다운로드 실패'));
  };

  // 원본 메일(.eml) 다운로드 — 메일 상세화면의 "원본 다운로드" 버튼에서 호출.
  // 서버가 IMAP/POP3에서 받아온 RFC822 원문을 그대로(mailparser로 파싱하지 않고) 내려준다.
  const downloadRawEml = (uid, subject) => {
    API.get(`/email/messages/${uid}/raw`, {
      params: { accountId: activeAccountId, folder }, responseType: 'blob',
    })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'message/rfc822' }));
        const a = document.createElement('a');
        const safeName = (subject || 'mail').replace(/[\\/:*?"<>|]/g, '_');
        a.href = blobUrl; a.download = `${safeName}.eml`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(blobUrl);
        logDownload('전자메일', `${safeName}.eml`);
      })
      .catch(() => showToast('원본 메일 다운로드 실패'));
  };

  const openCompose = (prefill) => {
    const acc = accounts.find(a => a.id === activeAccountId);
    setCompose({
      to: '', cc: '', bcc: '', subject: '', body: '', files: [],
      includeSignature: !!(acc && acc.signature_enabled && acc.signature_html), ...prefill,
    });
  };

  const replyTo = (m) => {
    const senderAddr = (m.from && m.from[0]) ? (m.from[0].address || '') : '';
    const quoted = `<br/><br/>---- 원본 메일 ----<br/>보낸사람: ${fmtAddr(m.from)}<br/>` +
      `날짜: ${fmtDate(m.date)}<br/>제목: ${m.subject}<br/><br/>${m.html || (m.text || '').replace(/\n/g, '<br/>')}`;
    openCompose({
      to: senderAddr,
      subject: m.subject?.startsWith('Re:') ? m.subject : `Re: ${m.subject || ''}`,
      body: quoted,
    });
  };

  const forward = (m) => {
    const quoted = `<br/><br/>---- 전달된 메일 ----<br/>보낸사람: ${fmtAddr(m.from)}<br/>` +
      `날짜: ${fmtDate(m.date)}<br/>제목: ${m.subject}<br/><br/>${m.html || (m.text || '').replace(/\n/g, '<br/>')}`;
    openCompose({
      subject: m.subject?.startsWith('Fwd:') ? m.subject : `Fwd: ${m.subject || ''}`,
      body: quoted,
    });
  };

  const sendMail = () => {
    if (!compose.to.trim()) { showToast('받는사람을 입력해주세요.'); return; }
    setSending(true);
    const form = new FormData();
    form.append('accountId', activeAccountId);
    form.append('to', compose.to);
    if (compose.cc) form.append('cc', compose.cc);
    if (compose.bcc) form.append('bcc', compose.bcc);
    form.append('subject', compose.subject || '');
    // 2026-09: 본문 붙여넣기 이미지 지원을 위해 일반 텍스트 입력창(textarea)에서 서명설정과
    // 동일한 이미지 첨부 가능 편집기(contentEditable)로 바꾸면서, compose.body 자체가 이미
    // HTML이 되었다(회신/전달 인용문도 원래 HTML이었음). 더 이상 <br/> 치환이 필요 없다.
    form.append('html', compose.body || '');
    form.append('includeSignature', compose.includeSignature ? 'true' : 'false');
    (compose.files || []).forEach(f => form.append('attachments', f));

    API.post('/email/send', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => {
        showToast('메일을 발송했습니다.');
        setCompose(null);
        // 보낸편지함(및 서명설정 등에 따라 반영되는 다른 폴더)의 캐시된 목록이 발송 전
        // 상태로 남아있지 않도록 이 계정 캐시를 무효화한다.
        mailSync.invalidateAccount(activeAccountId);
      })
      .catch(err => showToast(err?.response?.data?.message || '메일 발송 실패'))
      .finally(() => setSending(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ── 계정 미등록 ─────────────────────────────────────
  if (!accountsLoading && accounts.length === 0) {
    return (
      <div style={{ padding: 40 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>📧 전자메일</h2>
        <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0 0', marginBottom: 20 }}>
          아직 등록된 이메일 계정이 없습니다. 회사 메일서버 등 IMAP 또는 POP3(하이웍스 등
          IMAP 미지원 서비스) 계정 정보를 등록하면 여기서 메일함을 조회하고 메일을 보낼 수 있습니다.
        </p>
        <button onClick={() => setShowAccountModal(true)}
          style={{ padding: '10px 20px', background: '#185FA5', color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
          ⚙️ 이메일 계정 등록
        </button>
        {showAccountModal && (
          <AccountForm
            initial={null}
            onCancel={() => setShowAccountModal(false)}
            onSaved={() => { setShowAccountModal(false); loadAccounts(); }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', height: '100vh', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>📧 전자메일</h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>등록된 이메일 계정의 메일함을 조회하고 메일을 작성·발송합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => openCompose()} disabled={!activeAccountId} style={btnStyle('#185FA5')}>✏️ 메일쓰기</button>
          <button onClick={() => loadMessages(activeAccountId, folder, page, true)} style={btnStyle('#64748b')}>🔄 새로고침</button>
          <button onClick={() => setShowAddressBook(true)} style={btnStyle('#64748b')}>📇 주소록</button>
          <button onClick={() => setShowAccountModal(true)} style={btnStyle('#64748b')}>⚙️ 계정설정</button>
        </div>
      </div>

      {showAddressBook && <AddressBookModal onClose={() => setShowAddressBook(false)} />}

      {toast && (
        <div style={{ background: '#111827', color: '#fff', padding: '8px 14px', borderRadius: 6,
          marginBottom: 12, fontSize: 13, alignSelf: 'flex-start' }}>{toast}</div>
      )}

      {/* 계정 탭 — 등록된 이메일 계정마다 하나씩(탭 이름은 계정설정의 "탭에 표시할 이름", 기본값 메일주소) */}
      <div style={{ ...tabRowStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {accounts.map(a => (
            <button key={a.id} onClick={() => setActiveAccountId(a.id)}
              style={tabBtnStyle(activeAccountId === a.id)}>
              {a.label || a.email_address}
            </button>
          ))}
        </div>
      </div>

      {/* 폴더 탭 — 받은편지함/보낸편지함/보낼편지함/휴지통이 기본 구성이며, 계정이 이 4종을
          제공하지 못하면(POP3 등) 서버가 준 실제 폴더 이름을 그대로 탭으로 보여준다. POP3
          계정은 폴더가 받은편지함 하나뿐이라(folders.length === 1) 탭 줄 자체가 의미가 없으므로
          숨긴다 — IMAP처럼 폴더가 2개 이상일 때만 이 줄이 나타난다. */}
      {folders.length > 1 && (
        <div style={{ ...tabRowStyle, marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {folders.map(f => (
              <button key={f.path} onClick={() => changeFolder(f.path)} style={tabBtnStyle(folder === f.path)}>
                {f.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 메일 목록(제목)과 본문(내용) 사이의 구분선은 드래그로 폭을 조절할 수 있다.
          조절한 폭은 localStorage에 저장되어 다음에 메뉴를 열어도 그대로 유지된다. */}
      <div ref={splitContainerRef} style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* 좌측 메일 목록 */}
        <div style={{ width: listWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {listLoading && <div style={{ padding: 20, color: '#999', fontSize: 13 }}>불러오는 중...</div>}
            {listError && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{listError}</div>}
            {!listLoading && !listError && messages.length === 0 && (
              <div style={{ padding: 20, color: '#999', fontSize: 13 }}>메일이 없습니다.</div>
            )}
            {messages.map(m => (
              <div key={m.uid} onClick={() => openMessage(m.uid)}
                style={{
                  padding: '10px 8px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  background: detail?.uid === m.uid ? '#EAF3FC' : (m.seen ? '#fff' : '#F8FAFF'),
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666' }}>
                  <span style={{ fontWeight: m.seen ? 400 : 700 }}>{fmtAddr(m.from) || '(발신자 없음)'}</span>
                  <span>{fmtDate(m.date)}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: m.seen ? 400 : 700, marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.flagged ? '⭐ ' : ''}{m.hasAttachments ? '📎 ' : ''}{m.subject}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '10px 0', fontSize: 13 }}>
            <button disabled={page <= 1} onClick={() => changePage(page - 1)}
              style={pageBtnStyle(page <= 1)}>이전</button>
            <span style={{ color: '#666' }}>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => changePage(page + 1)}
              style={pageBtnStyle(page >= totalPages)}>다음</button>
          </div>
        </div>

        {/* 드래그 구분선 */}
        <div onMouseDown={startListResize} title="드래그해서 폭 조절" style={resizerStyle} />

        {/* 우측 상세보기 */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingLeft: 12 }}>
          {detailLoading && <div style={{ padding: 20, color: '#999' }}>불러오는 중...</div>}
          {!detailLoading && !detail && (
            <div style={{ padding: 40, color: '#999', textAlign: 'center' }}>메일을 선택해주세요.</div>
          )}
          {!detailLoading && detail && (
            <div style={{ padding: '4px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h3 style={{ margin: '0 0 10px' }}>{detail.subject}</h3>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => replyTo(detail)} style={btnStyle('#185FA5', true)}>↩ 답장</button>
                  <button onClick={() => forward(detail)} style={btnStyle('#64748b', true)}>➡ 전달</button>
                  <button onClick={() => toggleFlag(detail.uid, 'flagged',
                    !messages.find(m => m.uid === detail.uid)?.flagged)} style={btnStyle('#f59e0b', true)}>⭐</button>
                  {/* 원본 메일(.eml) 다운로드 — 2026-09 추가 */}
                  <button onClick={() => downloadRawEml(detail.uid, detail.subject)}
                    title="원본 메일을 .eml 파일로 다운로드" style={btnStyle('#0d9488', true)}>💾 원본 다운로드</button>
                  <button onClick={() => removeMessage(detail.uid)} style={btnStyle('#dc2626', true)}>🗑 삭제</button>
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>보낸사람: {fmtAddr(detail.from)}</div>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>받는사람: {fmtAddr(detail.to)}</div>
              {detail.cc && detail.cc.length > 0 && (
                <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>참조: {fmtAddr(detail.cc)}</div>
              )}
              <div style={{ fontSize: 12, color: '#999', marginBottom: 16 }}>{fmtDate(detail.date)}</div>

              {detail.attachments && detail.attachments.length > 0 && (
                <div style={{ marginBottom: 16, padding: 10, background: '#f8fafc', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                    📎 첨부파일 {detail.attachments.length}개
                  </div>
                  {detail.attachments.map(a => (
                    // 2026-09: 기존엔 이 줄(div) 전체가 클릭 영역이라 파일명 옆 빈 공간을 눌러도
                    // 다운로드가 되어버렸다. 파일명(+용량) 글자에만 클릭 영역이 붙도록
                    // inline-block span으로 감싸서 실제 텍스트를 클릭했을 때만 받아지게 한다.
                    <div key={a.index} style={{ padding: '3px 0' }}>
                      <span onClick={() => downloadAttachment(detail.uid, a.index, a.filename)}
                        style={{ fontSize: 13, color: '#185FA5', cursor: 'pointer', display: 'inline-block' }}>
                        {a.filename} ({Math.max(1, Math.round((a.size || 0) / 1024))}KB)
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {detail.html ? (
                <div style={{ borderTop: '1px solid #eee', paddingTop: 14 }}>
                  <MailHtmlFrame html={sanitizeHtml(detail.html)} />
                </div>
              ) : (
                <pre style={{ borderTop: '1px solid #eee', paddingTop: 14, fontSize: 14, lineHeight: 1.6,
                  whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{detail.text}</pre>
              )}
            </div>
          )}
        </div>
      </div>

      {compose && (
        <ComposeModal compose={compose} setCompose={setCompose} onClose={() => setCompose(null)}
          onSend={sendMail} sending={sending}
          account={accounts.find(a => a.id === activeAccountId) || null} />
      )}
      {showAccountModal && (
        accounts.length === 0 ? (
          <AccountForm
            initial={null}
            onCancel={() => setShowAccountModal(false)}
            onSaved={() => { setShowAccountModal(false); loadAccounts(); }}
          />
        ) : (
          <AccountsModal
            accounts={accounts}
            onClose={() => setShowAccountModal(false)}
            onChanged={loadAccounts}
          />
        )
      )}
    </div>
  );
}

function folderIcon(specialUse) {
  switch (specialUse) {
    case '\\Inbox': return '📥';
    case '\\Sent': return '📤';
    case '\\Trash': return '🗑️';
    case '\\Drafts': return '📝';
    case '\\Junk': return '🚫';
    default: return '📁';
  }
}

function btnStyle(bg, small) {
  return {
    padding: small ? '6px 10px' : '8px 16px', background: bg, color: '#fff', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: small ? 12 : 13, whiteSpace: 'nowrap',
  };
}
function pageBtnStyle(disabled) {
  return {
    padding: '4px 10px', border: '1px solid #ddd', borderRadius: 4, background: disabled ? '#f3f4f6' : '#fff',
    color: disabled ? '#aaa' : '#333', cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

// 다른 메뉴(업체관리/계약관리/인증관리 등)와 동일한 밑줄 탭 스타일 — 활성 탭만 파란색
// (#185FA5) 글자 + 2px 밑줄, 비활성은 회색(#888) + 투명 밑줄.
const tabRowStyle = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', borderBottom: '2px solid #e5e7eb' };
function tabBtnStyle(active) {
  return {
    padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 14, fontWeight: 600, color: active ? '#185FA5' : '#888',
    borderBottom: active ? '2px solid #185FA5' : '2px solid transparent',
    whiteSpace: 'nowrap', marginBottom: -2,
  };
}

// 메일 목록/본문 사이의 드래그 구분선 — 가운데 마우스오버시 살짝 진하게 보이도록 CSS만으로는
// hover를 표현하기 번거로워서 기본값을 옅은 회색으로 두고, cursor를 col-resize로 표시한다.
const resizerStyle = {
  width: 6, flexShrink: 0, alignSelf: 'stretch', cursor: 'col-resize',
  background: '#e5e7eb', borderRadius: 3, margin: '0 10px',
};

// 메일 본문(HTML)은 다른 사람이 보낸 콘텐츠이므로, 그대로 렌더링하기 전에 스크립트 실행
// 가능성이 있는 태그/속성만 최소한으로 제거한다(간단한 방어 — 완전한 새니타이저는 아니지만
// <script>, on*= 이벤트 핸들러, javascript: 링크 정도는 걸러낸다).
function sanitizeHtml(html) {
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/ on[a-z]+="[^"]*"/gi, '')
    .replace(/ on[a-z]+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

// 메일 본문은 격리된 iframe(srcDoc)에 렌더링한다. 메일 안의 <style>/@font-face/body 글꼴이
// 화면 전체(메뉴·제목 등)에 번져 글꼴이 깨지던 문제를 막기 위함. 스크립트는 sandbox로 차단하고,
// 링크는 새 창으로 열며, 높이는 내용에 맞춰 자동 조절한다.
function MailHtmlFrame({ html }) {
  const ref = useRef(null);
  const [h, setH] = useState(200);
  const doc = '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">'
    + '<style>html,body{margin:0;padding:0;}body{font-family:"Pretendard","Malgun Gothic","맑은 고딕",Arial,sans-serif;'
    + 'font-size:14px;line-height:1.6;color:#222;word-break:break-word;overflow-wrap:anywhere;}'
    + 'img{max-width:100%;height:auto;}table{max-width:100%;}</style></head><body>' + html + '</body></html>';
  const fit = () => {
    try {
      const d = ref.current && ref.current.contentDocument;
      if (!d || !d.body) return;
      setH(Math.max(60, Math.max(d.body.scrollHeight, d.documentElement.scrollHeight)) + 4);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    const t = setInterval(fit, 700); // 이미지 지연 로딩 대응
    return () => clearInterval(t);
  }, [html]);
  return (
    <iframe ref={ref} title="mail-body" srcDoc={doc} onLoad={fit}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', height: h, border: 'none', display: 'block', background: '#fff' }} />
  );
}

// ── 메일 작성 모달 ──────────────────────────────────────────
// 2026-09 요청: "이미지 붙여넣기 기능 추가" — 본문 입력창을 일반 텍스트 textarea에서, 서명
// 편집기와 동일한 방식(Ctrl+V로 이미지 붙여넣기 → 즉시 서버 업로드 → img[data-filename] →
// 발송 시점에 백엔드가 cid 첨부로 변환)의 contentEditable 편집기로 바꾼다. compose.body는
// 이제 항상 HTML이다(회신/전달 인용문도 원래부터 HTML이었으므로 그대로 호환된다).
function ComposeModal({ compose, setCompose, onClose, onSend, sending, account }) {
  const update = (k, v) => setCompose(c => ({ ...c, [k]: v }));
  const hasSignature = !!(account && account.signature_enabled && account.signature_html);

  // 2026-09 요청: "받는사람/참조/숨은참조 우측 끝에 주소록 버튼 추가" — 클릭하면 주소록
  // 목록을 보여주고 선택한 항목의 이메일을 해당 필드에 자동으로 채워넣는다(콤마로 여러명
  // 구분되는 기존 입력 방식과 호환되도록, 이미 값이 있으면 뒤에 이어붙인다).
  const [pickerField, setPickerField] = useState(null); // 'to' | 'cc' | 'bcc' | null
  const appendAddress = (field, email) => {
    setCompose(c => {
      const existing = (c[field] || '').trim();
      const parts = existing ? existing.split(',').map(s => s.trim()).filter(Boolean) : [];
      if (!parts.includes(email)) parts.push(email);
      return { ...c, [field]: parts.join(', ') };
    });
  };

  // 2026-09 후속 요청: "받는사람/참조/숨은참조에 이름 또는 회사명을 입력하면 아래쪽에
  // 자동으로 검색해서 나타나게" — 버튼을 눌러 모달을 여는 대신, 입력하는 즉시 그 필드
  // 바로 아래에 이름/이메일/회사명이 일치하는 주소록 항목을 보여주고 클릭으로 채워넣는다.
  // 주소록은 모달을 열 때 한 번만 불러와 세 필드가 함께 사용한다.
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    API.get('/email/contacts').then(res => setContacts(res.data || [])).catch(() => {});
  }, []);

  const bodyRef = useRef(null);
  const bodyLoadedRef = useRef(false);
  // 회의록 편집기와 동일한 이유로 필요 — 붙여넣기마다 시작되는 업로드가 아직 끝나지 않은
  // 채로 "보내기"를 누르면 죽은 blob: 주소만 있는 img가 그대로 발송될 수 있어서, 여기 모아뒀다가
  // 보내기 직전에 전부 끝날 때까지 기다린다(2026-09, "메모/회의록 이미지 저장 경쟁조건" 수정과
  // 동일한 패턴을 이번에 새로 추가하는 본문 이미지 붙여넣기에도 처음부터 적용).
  const pendingImageUploadsRef = useRef([]);

  useEffect(() => {
    if (bodyLoadedRef.current || !bodyRef.current) return;
    bodyLoadedRef.current = true;
    bodyRef.current.innerHTML = compose.body || '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBodyInput = () => {
    if (bodyRef.current) update('body', bodyRef.current.innerHTML);
  };

  const insertBodyImage = (imageFile) => {
    const localUrl = window.URL.createObjectURL(imageFile);
    document.execCommand('insertImage', false, localUrl);
    const imgs = bodyRef.current ? bodyRef.current.querySelectorAll(`img[src="${localUrl}"]`) : [];
    const insertedImg = imgs[imgs.length - 1];
    if (insertedImg) { insertedImg.style.maxWidth = '100%'; insertedImg.style.height = 'auto'; }
    const uploadPromise = (async () => {
      try {
        const fd = new FormData();
        fd.append('image', imageFile, imageFile.name || 'pasted.png');
        const res = await API.post('/email/body-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (insertedImg && res.data.filename) insertedImg.setAttribute('data-filename', res.data.filename);
      } catch (e) { /* 업로드 실패해도 화면 미리보기는 남아있음 */ }
      handleBodyInput();
    })();
    pendingImageUploadsRef.current.push(uploadPromise);
  };

  const handleBodyPaste = (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) { imageFile = items[i].getAsFile(); break; }
    }
    if (!imageFile) return; // 이미지가 아니면 기본 붙여넣기 동작 사용
    e.preventDefault();
    insertBodyImage(imageFile);
  };

  const handleBodyDrop = (e) => {
    const files = e.dataTransfer && e.dataTransfer.files;
    if (!files || !files.length) return;
    const imageFile = Array.from(files).find(f => f.type && f.type.startsWith('image/'));
    if (!imageFile) return; // 이미지가 아닌 파일은 기존 첨부파일 드롭존에서 처리
    e.preventDefault();
    insertBodyImage(imageFile);
  };

  const handleSend = async () => {
    if (pendingImageUploadsRef.current.length) {
      await Promise.all(pendingImageUploadsRef.current);
    }
    onSend();
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 860 }}>
        <div style={modalHeaderStyle}>
          <span>✏️ 메일 작성</span>
          <span onClick={onClose} style={closeXStyle}>✕</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <AddressAutocompleteInput placeholder="받는사람 (콤마로 여러명 구분, 이름/회사명 입력시 검색)"
              value={compose.to} onChange={v => update('to', v)} contacts={contacts} />
            <button type="button" onClick={() => setPickerField('to')} style={btnStyle('#64748b', true)}>📇 주소록</button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <AddressAutocompleteInput placeholder="참조(CC)"
              value={compose.cc} onChange={v => update('cc', v)} contacts={contacts} />
            <button type="button" onClick={() => setPickerField('cc')} style={btnStyle('#64748b', true)}>📇 주소록</button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <AddressAutocompleteInput placeholder="숨은참조(BCC)"
              value={compose.bcc} onChange={v => update('bcc', v)} contacts={contacts} />
            <button type="button" onClick={() => setPickerField('bcc')} style={btnStyle('#64748b', true)}>📇 주소록</button>
          </div>
          <input placeholder="제목" value={compose.subject}
            onChange={e => update('subject', e.target.value)} style={inputStyle} />
          <div>
            <div style={{ fontSize: 10, color: '#aaa', marginBottom: 3 }}>
              이미지를 복사해서 붙여넣거나(Ctrl+V), 파일을 바로 드래그해서 본문에 넣을 수 있습니다
            </div>
            <div
              ref={bodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleBodyInput}
              onPaste={handleBodyPaste}
              onDrop={handleBodyDrop}
              onDragOver={e => e.preventDefault()}
              data-placeholder="내용을 입력하세요"
              className="email-compose-body"
              style={{ ...inputStyle, height: 260, overflowY: 'auto', fontFamily: 'inherit',
                lineHeight: 1.6, cursor: 'text' }}
            />
          </div>

          {/* 2026-08: 다른 메뉴(업체관리/공지사항 등)와 동일한 드래그 앤 드롭 첨부파일 UI로 전환 */}
          <div>
            <FileDropZone multiple fileNames={(compose.files || []).map(f => f.name)}
              onFiles={files => update('files', [...(compose.files || []), ...files])} />
            {(compose.files || []).map((f, i) => (
              <div key={i} style={{ fontSize: 12, color: '#555', display: 'flex', justifyContent: 'space-between',
                padding: '3px 0' }}>
                <span>📎 {f.name} ({Math.round(f.size / 1024)}KB)</span>
                <span onClick={() => update('files', compose.files.filter((_, idx) => idx !== i))}
                  style={{ color: '#dc2626', cursor: 'pointer' }}>제거</span>
              </div>
            ))}
          </div>

          {/* 2026-08 서명설정: 계정에 서명이 등록되어 있을 때만 노출 — 발송 시 본문 아래에 붙는다 */}
          {hasSignature && (
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#555' }}>
                <input type="checkbox" checked={!!compose.includeSignature}
                  onChange={e => update('includeSignature', e.target.checked)} />
                ✍️ 서명 포함
              </label>
              {compose.includeSignature && (
                <div style={{ border: '1px solid #eee', borderRadius: 6, padding: '8px 10px', marginTop: 6, background: '#fafafa' }}>
                  <SignaturePreview html={account.signature_html} />
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button onClick={onClose} style={btnStyle('#94a3b8')}>취소</button>
            <button onClick={handleSend} disabled={sending} style={btnStyle('#185FA5')}>
              {sending ? '보내는 중...' : '보내기'}
            </button>
          </div>
        </div>
      </div>
      {pickerField && (
        <ContactPickerModal
          onSelect={(email) => { appendAddress(pickerField, email); setPickerField(null); }}
          onClose={() => setPickerField(null)}
        />
      )}
    </div>
  );
}

// ── 받는사람/참조/숨은참조 자동완성 입력창 ────────────────────────────
// 2026-09 후속 요청: "이름 또는 회사명을 입력하면 아래쪽에 자동으로 검색해서 나타나게" —
// 콤마로 여러 명을 구분하는 기존 입력 방식은 그대로 두고, 마지막 콤마 뒤에 지금 타이핑
// 중인 조각(이름/이메일/회사명)만 주소록과 매칭해 바로 아래 드롭다운으로 보여준다.
// 항목을 클릭하면 그 조각을 이메일로 치환하고(앞의 기존 입력은 그대로 유지) 드롭다운을 닫는다.
function AddressAutocompleteInput({ value, onChange, placeholder, contacts }) {
  const [open, setOpen] = useState(false);
  // 2026-09 후속 요청: "키보드 아래쪽 버튼을 눌러 선택 후 엔터를 누르면 강용진 대표
  // (메일주소) 이렇게 나오게" — 방향키로 후보를 순회할 수 있는 하이라이트 인덱스.
  const [highlight, setHighlight] = useState(-1);

  // "김철수, 이영" 처럼 이미 완성된 앞부분(prefix)과 지금 타이핑 중인 마지막 조각(fragment)을
  // 나눈다 — 검색은 fragment만 대상으로 하고, 선택 시 prefix + 선택된 이메일로 교체한다.
  const lastComma = value.lastIndexOf(',');
  const prefix = lastComma >= 0 ? value.slice(0, lastComma + 1) + ' ' : '';
  const fragment = (lastComma >= 0 ? value.slice(lastComma + 1) : value).trim();

  const suggestions = fragment
    ? contacts.filter(c => {
        const q = fragment.toLowerCase();
        return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
          || (c.company || '').toLowerCase().includes(q) || (c.position || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  // 선택 시 "이름 직급 <이메일>" 형식으로 채워 넣는다 — 표시는 이름/직급이 함께 보이면서도
  // <이메일> 꺾쇠괄호 표기(RFC 5322 표준 표기법)라 서버의 nodemailer가 그대로 발신 주소로
  // 파싱할 수 있다(괄호 "(메일주소)" 형태는 메일 발송 시 파싱되지 않아 실제 발송이 깨진다).
  const selectContact = (c) => {
    const namePart = [c.name, c.position].filter(Boolean).join(' ');
    const formatted = namePart ? `${namePart} <${c.email}>` : c.email;
    onChange(prefix + formatted);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        selectContact(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <input placeholder={placeholder} value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => { setOpen(false); setHighlight(-1); }, 150)}
        style={{ ...inputStyle, width: '100%' }} />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, zIndex: 1200,
          background: '#fff', border: '1px solid #ddd', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
        }}>
          {suggestions.map((c, i) => (
            <div key={c.id}
              onMouseDown={(e) => { e.preventDefault(); selectContact(c); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 13,
                background: i === highlight ? '#eff6ff' : '#fff',
              }}>
              <span style={{ fontWeight: 600, color: '#222' }}>{c.name || '(이름없음)'}</span>
              {c.position && <span style={{ color: '#2563eb', fontSize: 12 }}> {c.position}</span>}
              <span style={{ color: '#666' }}> · {c.email}{c.company ? ` · ${c.company}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 주소록 선택 팝업 — 메일작성 화면의 받는사람/참조/숨은참조 옆 "📇 주소록" 버튼에서
// 연다. 클릭 한 번으로 목록에서 골라 해당 필드에 이메일을 채워넣는 용도라, 등록/수정/
// 삭제 같은 관리 기능은 없다(그건 AddressBookModal의 역할).
function ContactPickerModal({ onSelect, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    API.get('/email/contacts')
      .then(res => setContacts(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = contacts.filter(c => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ ...overlayStyle, zIndex: 1100 }}>
      <div style={{ ...modalStyle, width: 420 }}>
        <div style={modalHeaderStyle}>
          <span>📇 주소록에서 선택</span>
          <span onClick={onClose} style={closeXStyle}>✕</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input placeholder="이름/이메일/회사 검색" value={search} onChange={e => setSearch(e.target.value)} style={inputStyle} />
          <div style={{ border: '1px solid #eee', borderRadius: 8, maxHeight: '55vh', overflowY: 'auto' }}>
            {loading && <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>불러오는 중...</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>등록된 주소록이 없습니다.</div>
            )}
            {!loading && filtered.map(c => (
              <div key={c.id} onClick={() => onSelect(c.email)}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{c.name || '(이름없음)'}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{c.email}{c.company ? ` · ${c.company}` : ''}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// 서명 미리보기 — 서명 편집기에서 img[data-filename]으로 저장해둔 이미지를 다시 불러와서
// 보여준다(메일작성 화면에서 "이 서명이 이렇게 첨부됩니다"를 확인할 수 있도록).
function SignaturePreview({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = html || '';
    const imgs = Array.from(ref.current.querySelectorAll('img[data-filename]'));
    imgs.forEach(img => {
      const filename = img.getAttribute('data-filename');
      if (!filename) return;
      API.get('/email/signature-image/' + filename, { responseType: 'blob' })
        .then(res => { img.src = window.URL.createObjectURL(res.data); })
        .catch(() => {});
    });
  }, [html]);
  return <div ref={ref} style={{ fontSize: 13, lineHeight: 1.6 }} />;
}

// ── 계정관리 모달(목록) — 등록된 계정을 목록으로 보여주고 추가/수정/해제한다 ──────
function AccountsModal({ accounts, onClose, onChanged }) {
  const [mode, setMode] = useState('list'); // 'list' | 'form'
  const [editing, setEditing] = useState(null); // 수정 대상 계정(신규면 null)

  const openAdd = () => { setEditing(null); setMode('form'); };
  const openEdit = (acc) => { setEditing(acc); setMode('form'); };
  const backToList = () => { setMode('list'); setEditing(null); };

  const removeAccount = (acc) => {
    if (!window.confirm(`"${acc.label || acc.email_address}" 계정 연동을 해제할까요? (등록된 접속 정보가 삭제됩니다)`)) return;
    API.delete(`/email/accounts/${acc.id}`).then(() => onChanged()).catch(() => window.alert('연동 해제에 실패했습니다.'));
  };

  if (mode === 'form') {
    return (
      <AccountForm
        initial={editing}
        onCancel={backToList}
        onSaved={() => { onChanged(); backToList(); }}
      />
    );
  }

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 480 }}>
        <div style={modalHeaderStyle}>
          <span>⚙️ 이메일 계정관리</span>
          <span onClick={onClose} style={closeXStyle}>✕</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh', overflowY: 'auto' }}>
          {accounts.length === 0 && (
            <div style={{ fontSize: 13, color: '#999', padding: '20px 0', textAlign: 'center' }}>
              등록된 이메일 계정이 없습니다.
            </div>
          )}
          {accounts.map(acc => (
            <div key={acc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', border: '1px solid #eee', borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{acc.label || acc.email_address}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                  {acc.email_address} · {acc.protocol === 'pop3' ? 'POP3' : 'IMAP'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => openEdit(acc)} style={btnStyle('#64748b', true)}>수정</button>
                <button onClick={() => removeAccount(acc)} style={btnStyle('#dc2626', true)}>해제</button>
              </div>
            </div>
          ))}
          <button onClick={openAdd} style={{ ...btnStyle('#185FA5'), marginTop: 4 }}>+ 계정 추가</button>
        </div>
      </div>
    </div>
  );
}

// ── 계정 추가/수정 폼 ──────────────────────────────────────
function AccountForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    email_address: initial?.email_address || '',
    label: initial?.label || '',
    display_name: initial?.display_name || '',
    protocol: initial?.protocol || 'imap', // 2026-08: 하이웍스 등 IMAP 미지원 메일서비스를 위한 POP3 지원 추가
    imap_host: initial?.imap_host || '',
    imap_port: initial?.imap_port || 993,
    imap_secure: initial ? !!initial.imap_secure : true,
    smtp_host: initial?.smtp_host || '',
    smtp_port: initial?.smtp_port || 465,
    smtp_secure: initial ? !!initial.smtp_secure : true,
    login_user: initial?.login_user || '',
    password: '',
    signature_enabled: initial ? !!initial.signature_enabled : false, // 2026-08 서명설정 추가
    signature_html: initial?.signature_html || '',
  });
  const [testMsg, setTestMsg] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const isPop3 = form.protocol === 'pop3';

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── 서명 편집기(이미지 포함 가능) — 공지사항 내용 편집기와 동일한 방식으로, 이미지를
  // 붙여넣거나 드래그하면 서버에 업로드하고 img[data-filename]으로 남겨둔다. 발송 시점에
  // 백엔드가 그 파일을 실제 첨부(cid)로 바꿔서 이미지가 보이도록 처리한다.
  const sigRef = useRef(null);
  const sigLoadedRef = useRef(false);
  useEffect(() => {
    if (sigLoadedRef.current || !sigRef.current) return;
    sigLoadedRef.current = true;
    sigRef.current.innerHTML = form.signature_html || '';
    hydrateSignatureImages();
    // eslint-disable-next-line
  }, []);

  const hydrateSignatureImages = async () => {
    if (!sigRef.current) return;
    const imgs = Array.from(sigRef.current.querySelectorAll('img[data-filename]'));
    for (const img of imgs) {
      const filename = img.getAttribute('data-filename');
      if (!filename) continue;
      try {
        const res = await API.get('/email/signature-image/' + filename, { responseType: 'blob' });
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 못 불러와도 나머지는 그대로 둔다 */ }
    }
  };

  const handleSigInput = () => {
    if (sigRef.current) update('signature_html', sigRef.current.innerHTML);
  };

  const insertSignatureImage = async (imageFile) => {
    const localUrl = window.URL.createObjectURL(imageFile);
    document.execCommand('insertImage', false, localUrl);
    const imgs = sigRef.current ? sigRef.current.querySelectorAll(`img[src="${localUrl}"]`) : [];
    const insertedImg = imgs[imgs.length - 1];
    // 2026-08 요청 — 서명 이미지 가로폭을 700px로 고정하고, 세로는 원본 비율에 맞춰
    // 자동으로 늘어나도록(height:auto) 변경했다.
    if (insertedImg) { insertedImg.style.width = '700px'; insertedImg.style.height = 'auto'; }
    try {
      const fd = new FormData();
      fd.append('image', imageFile, imageFile.name || 'signature.png');
      const res = await API.post('/email/signature-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (insertedImg && res.data.filename) insertedImg.setAttribute('data-filename', res.data.filename);
    } catch (e) { /* 업로드 실패해도 화면 미리보기는 남아있음(저장 후 다시 열면 안 보일 수 있음) */ }
    handleSigInput();
  };

  const handleSigPaste = (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) { imageFile = items[i].getAsFile(); break; }
    }
    if (!imageFile) return;
    e.preventDefault();
    insertSignatureImage(imageFile);
  };

  const handleSigDrop = (e) => {
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    e.preventDefault();
    insertSignatureImage(file);
  };

  // 수신 프로토콜을 바꾸면 포트 기본값도 그에 맞춰 바꿔준다(IMAP 993 / POP3 995).
  // 사용자가 포트를 직접 수정한 적이 없을 때만(기본값 그대로일 때만) 자동 전환한다.
  const changeProtocol = (proto) => {
    setForm(f => {
      const wasDefaultPort = String(f.imap_port) === '993' || String(f.imap_port) === '995';
      return {
        ...f,
        protocol: proto,
        imap_port: wasDefaultPort ? (proto === 'pop3' ? 995 : 993) : f.imap_port,
      };
    });
  };

  const testConnection = () => {
    setTesting(true); setTestMsg('');
    API.post('/email/account/test', {
      protocol: form.protocol,
      imap_host: form.imap_host, imap_port: Number(form.imap_port), imap_secure: form.imap_secure,
      login_user: form.login_user, password: form.password,
    })
      .then(res => setTestMsg('✅ ' + res.data.message))
      .catch(err => setTestMsg('❌ ' + (err?.response?.data?.message || '접속 실패')))
      .finally(() => setTesting(false));
  };

  const save = () => {
    setSaving(true); setSaveMsg('');
    const req = initial ? API.put(`/email/accounts/${initial.id}`, form) : API.post('/email/accounts', form);
    req
      .then(() => onSaved())
      .catch(err => setSaveMsg('❌ ' + (err?.response?.data?.message || '저장 실패')))
      .finally(() => setSaving(false));
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 480 }}>
        <div style={modalHeaderStyle}>
          <span>⚙️ {initial ? '이메일 계정 수정' : '이메일 계정 추가'}</span>
          <span onClick={onCancel} style={closeXStyle}>✕</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh', overflowY: 'auto' }}>
          <label style={labelStyle}>이메일 주소</label>
          <input value={form.email_address} onChange={e => update('email_address', e.target.value)} style={inputStyle} />

          <label style={labelStyle}>탭에 표시할 이름 (선택 — 비워두면 메일 주소로 표시됩니다)</label>
          <input value={form.label} onChange={e => update('label', e.target.value)} style={inputStyle}
            placeholder={form.email_address || '예: 회사메일'} />

          <label style={labelStyle}>보낸사람 표시 이름 (선택)</label>
          <input value={form.display_name} onChange={e => update('display_name', e.target.value)} style={inputStyle} />

          <label style={labelStyle}>로그인 아이디 (대개 이메일 주소와 동일)</label>
          <input value={form.login_user} onChange={e => update('login_user', e.target.value)} style={inputStyle} />

          <label style={labelStyle}>비밀번호(또는 앱 비밀번호) {initial ? '— 변경할 때만 입력' : ''}</label>
          <input type="password" value={form.password} onChange={e => update('password', e.target.value)} style={inputStyle} />

          <label style={labelStyle}>수신 프로토콜</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => changeProtocol('imap')}
              style={{ ...protoBtnStyle, ...(form.protocol !== 'pop3' ? protoBtnActiveStyle : {}) }}>
              IMAP (일반)
            </button>
            <button type="button" onClick={() => changeProtocol('pop3')}
              style={{ ...protoBtnStyle, ...(isPop3 ? protoBtnActiveStyle : {}) }}>
              POP3 (하이웍스 등)
            </button>
          </div>
          {isPop3 && (
            <div style={{ fontSize: 11, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px' }}>
              POP3는 폴더 구분이 없어 받은편지함 하나로만 조회되고, 서버에 읽음/중요표시가 저장되지
              않아 읽음 여부만 IMS에서 별도로 기억합니다. 삭제는 휴지통 없이 즉시 영구 삭제됩니다.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{isPop3 ? 'POP3 서버 주소' : 'IMAP 서버 주소'}</label>
              <input value={form.imap_host} onChange={e => update('imap_host', e.target.value)} style={inputStyle}
                placeholder={isPop3 ? '예: pop3s.hiworks.com' : '예: imap.company.com'} />
            </div>
            <div style={{ width: 90 }}>
              <label style={labelStyle}>포트</label>
              <input value={form.imap_port} onChange={e => update('imap_port', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.imap_secure} onChange={e => update('imap_secure', e.target.checked)} />
            {isPop3 ? 'POP3' : 'IMAP'} TLS/SSL 사용
          </label>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>SMTP 서버 주소</label>
              <input value={form.smtp_host} onChange={e => update('smtp_host', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ width: 90 }}>
              <label style={labelStyle}>포트</label>
              <input value={form.smtp_port} onChange={e => update('smtp_port', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.smtp_secure} onChange={e => update('smtp_secure', e.target.checked)} />
            SMTP TLS/SSL 사용
          </label>

          {/* 2026-08 서명설정 추가 — 이미지도 넣을 수 있는 서명(발송 시 본문 아래에 자동 첨부) */}
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <input type="checkbox" checked={form.signature_enabled}
              onChange={e => update('signature_enabled', e.target.checked)} />
            ✍️ 메일 작성 시 서명 자동 포함
          </label>
          {form.signature_enabled && (
            <div>
              <label style={{ ...labelStyle, marginBottom: 4 }}>
                서명 내용 <span style={{ fontSize: 10, color: '#aaa' }}>(이미지를 복사해서 붙여넣거나(Ctrl+V), 파일을 바로 드래그해서 넣을 수 있습니다)</span>
              </label>
              <div
                ref={sigRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleSigInput}
                onPaste={handleSigPaste}
                onDrop={handleSigDrop}
                onDragOver={e => e.preventDefault()}
                style={{ ...inputStyle, height: 110, overflowY: 'auto', lineHeight: 1.6, cursor: 'text' }}
              />
            </div>
          )}

          {testMsg && <div style={{ fontSize: 12, marginTop: 4 }}>{testMsg}</div>}
          {saveMsg && <div style={{ fontSize: 12, color: '#dc2626' }}>{saveMsg}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button onClick={testConnection} disabled={testing} style={btnStyle('#64748b', true)}>
              {testing ? '접속 테스트 중...' : '연결 테스트'}
            </button>
            <button onClick={onCancel} style={btnStyle('#94a3b8', true)}>취소</button>
            <button onClick={save} disabled={saving} style={btnStyle('#185FA5', true)}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 주소록 모달 ──────────────────────────────────────────────
// 2026-09 요청: "계정설정 왼쪽에 주소록 버튼 추가" — 4가지 요구사항 구현.
//  1) 업체관리에 등록된 담당자 이메일을 기본으로 가져온다 — 업체 등록/수정 시점에 백엔드가
//     자동으로 동기화하므로(routes/companies.js), 이 화면에는 더 이상 수동 "가져오기" 버튼이
//     없다(2026-09 후속 요청으로 삭제 — "업체관리에서 가져오기 버튼은 삭제").
//  2) "+ 직접등록" 버튼으로 여는 입력폼에서 수기로 등록할 수 있다.
//  3) 타 메일에서 내보낸 CSV 또는 vCard(.vcf) 파일을 업로드하면 일괄 등록된다.
//  4) 공용(전체 공유)/개인관리(본인만) 여부를 체크박스로 선택할 수 있고, 목록에서도 구분해
//     보여준다. 개인관리 항목은 등록자 본인만 수정/삭제할 수 있다(백엔드에서도 재검증).
function AddressBookModal({ onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // 수정 중인 contact row(없으면 신규등록)
  const [form, setForm] = useState({ name: '', email: '', company: '', position: '', phone: '', note: '', is_shared: true });
  const [importShared, setImportShared] = useState(true);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(() => {
    setLoading(true);
    API.get('/email/contacts')
      .then(res => setContacts(res.data || []))
      .catch(() => showToast('주소록을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = contacts.filter(c => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q);
  });

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', email: '', company: '', position: '', phone: '', note: '', is_shared: true });
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || '', email: c.email || '', company: c.company || '', position: c.position || '',
      phone: c.phone || '', note: c.note || '', is_shared: !!c.is_shared });
    setShowForm(true);
  };

  const saveForm = () => {
    if (!form.email || !form.email.trim()) { window.alert('이메일 주소를 입력해주세요.'); return; }
    const body = { ...form, is_shared: form.is_shared ? 1 : 0 };
    const req = editing ? API.put(`/email/contacts/${editing.id}`, body) : API.post('/email/contacts', body);
    req.then(() => { setShowForm(false); load(); showToast(editing ? '주소록을 수정했습니다.' : '주소록에 등록했습니다.'); })
      .catch((e) => window.alert(e?.response?.data?.message || '저장에 실패했습니다.'));
  };

  const removeContact = (c) => {
    if (!window.confirm(`'${c.name || c.email}' 항목을 주소록에서 삭제할까요?`)) return;
    API.delete(`/email/contacts/${c.id}`)
      .then(() => { load(); showToast('주소록에서 삭제했습니다.'); })
      .catch((e) => window.alert(e?.response?.data?.message || '삭제에 실패했습니다.'));
  };

  const importFile = (file) => {
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('is_shared', importShared ? '1' : '0');
    API.post('/email/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => { load(); showToast(res.data?.message || '가져왔습니다.'); })
      .catch((e) => window.alert(e?.response?.data?.message || '가져오기에 실패했습니다.'))
      .finally(() => setImporting(false));
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 640 }}>
        <div style={modalHeaderStyle}>
          <span>📇 주소록</span>
          <span onClick={onClose} style={closeXStyle}>✕</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '78vh', overflowY: 'auto' }}>
          {toast && (
            <div style={{ background: '#111827', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>{toast}</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input placeholder="이름/이메일/회사 검색" value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
            <button onClick={openNew} style={btnStyle('#185FA5', true)}>+ 직접등록</button>
          </div>

          {/* CSV/vCard/엑셀 파일 업로드 — 타 메일에서 내보낸 주소록 또는 엑셀 명단을 그대로
              업로드하면 일괄 등록된다. 2026-09 요청: 엑셀(.xlsx) 지원 추가 + 등록 시 중복 제외 */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555', cursor: 'pointer' }}>
                <input type="checkbox" checked={importShared} onChange={e => setImportShared(e.target.checked)} />
                가져온 항목을 공용으로 등록(해제 시 나만 보는 개인 주소록으로 등록)
              </label>
            </div>
            <FileDropZone accept=".csv,.vcf,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              fileName={importing ? '가져오는 중...' : ''}
              onFile={(f) => importFile(f)}
              style={{ marginBottom: 4 }} />
            <div style={{ fontSize: 11, color: '#999' }}>CSV, 엑셀(.xlsx) 또는 vCard(.vcf) 파일을 지원합니다(다음/네이버/아웃룩 등에서 내보낸 파일, 또는 이름/회사명/이메일 등을 정리한 엑셀 명단). 중복된 이메일은 자동으로 제외하고 등록됩니다.</div>
          </div>

          {/* 신규 등록 폼 — "+ 직접등록" 클릭 시 여기(목록 위)에 표시. 기존 항목 수정 폼은
              2026-09 요청("수정을 누르면 해당 주소록 리스트 아래쪽에 수정할 내용을 표시")에 따라
              더 이상 여기 표시하지 않고, 아래 목록에서 해당 행 바로 밑에 인라인으로 표시한다. */}
          {showForm && !editing && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>주소록 직접등록</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="이름" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="직급" value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="이메일 *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="회사명" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="연락처" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
              </div>
              <input placeholder="메모" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={inputStyle} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_shared} onChange={e => setForm(f => ({ ...f, is_shared: e.target.checked }))} />
                전체 회원에게 공용으로 공유(해제 시 나만 보는 개인 주소록)
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowForm(false)} style={btnStyle('#94a3b8', true)}>취소</button>
                <button onClick={saveForm} style={btnStyle('#185FA5', true)}>등록</button>
              </div>
            </div>
          )}

          {/* 목록 */}
          <div style={{ border: '1px solid #eee', borderRadius: 8 }}>
            {loading && <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>불러오는 중...</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>등록된 주소록이 없습니다.</div>
            )}
            {!loading && filtered.map(c => (
              <Fragment key={c.id}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderBottom: '1px solid #f1f5f9', gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#222', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{c.name || '(이름없음)'}</span>
                      {c.position && <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>{c.position}</span>}
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                        background: c.is_shared ? '#EAF3FC' : '#FEF3C7', color: c.is_shared ? '#185FA5' : '#B45309',
                      }}>{c.is_shared ? '공용' : '개인'}</span>
                      {c.source === 'company' && (
                        <span style={{ fontSize: 10, color: '#0d9488' }}>🏢 업체관리 연동</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>{c.email}{c.company ? ` · ${c.company}` : ''}{c.phone ? ` · ${c.phone}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => (editing && editing.id === c.id ? setShowForm(false) : openEdit(c))} style={btnStyle('#64748b', true)}>
                      {editing && editing.id === c.id && showForm ? '닫기' : '수정'}
                    </button>
                    <button onClick={() => removeContact(c)} style={btnStyle('#dc2626', true)}>삭제</button>
                  </div>
                </div>
                {/* 수정 폼 — 요청대로 해당 행 바로 아래에 인라인으로 표시 */}
                {showForm && editing && editing.id === c.id && (
                  <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 12,
                    background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>주소록 수정</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input placeholder="이름" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                      <input placeholder="직급" value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                      <input placeholder="이메일 *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input placeholder="회사명" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                      <input placeholder="연락처" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                    </div>
                    <input placeholder="메모" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={inputStyle} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.is_shared} onChange={e => setForm(f => ({ ...f, is_shared: e.target.checked }))} />
                      전체 회원에게 공용으로 공유(해제 시 나만 보는 개인 주소록)
                    </label>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button onClick={() => setShowForm(false)} style={btnStyle('#94a3b8', true)}>취소</button>
                      <button onClick={saveForm} style={btnStyle('#185FA5', true)}>수정</button>
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: '#fff', borderRadius: 10, maxWidth: '90vw', maxHeight: '90vh', overflow: 'hidden',
  display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
const modalHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px',
  borderBottom: '1px solid #eee', fontWeight: 700, fontSize: 15,
};
const closeXStyle = { cursor: 'pointer', color: '#999', fontSize: 16 };
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13,
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, color: '#666', marginBottom: -2 };
const protoBtnStyle = {
  flex: 1, padding: '7px 0', border: '1px solid #ddd', borderRadius: 6, background: '#fff',
  color: '#666', fontSize: 12, cursor: 'pointer',
};
const protoBtnActiveStyle = { background: '#185FA5', borderColor: '#185FA5', color: '#fff', fontWeight: 600 };


// ── 시스템메일/외부메일 전환 래퍼 ─────────────────────────────────────────
// 환경설정 > 회사정보에서 "시스템메일"을 선택하면 자체 메일함(SysMail)을 보여주고, "외부메일"이면(기본값)
// 기존 전자메일 화면(ExternalEmail)을 그대로 보여준다. 시스템메일 사용 중에도 외부 계정으로 잠시 전환할 수 있다.
export default function Email() {
  const [status, setStatus] = useState(null);
  const [ext, setExt] = useState(() => sessionStorage.getItem('email_view_external') === '1');
  const load = useCallback(() => {
    API.get('/sysmail/status').then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const setView = (v) => { setExt(v); try { sessionStorage.setItem('email_view_external', v ? '1' : '0'); } catch (e) { /* ignore */ } };
  if (!status) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>불러오는 중...</div>;
  if (!status.enabled) return <ExternalEmail />;
  if (ext) {
    return (
      <div>
        <div style={{ marginBottom: 8 }}><button onClick={() => setView(false)} style={{ padding: '6px 14px', border: '1px solid #185FA5', color: '#185FA5', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>← 시스템 메일함으로</button></div>
        <ExternalEmail />
      </div>
    );
  }
  return <SysMail status={status} onSwitchExternal={() => setView(true)} onStatusChange={load} />;
}
